import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSubscriptionByToken, checkoutSubscription, tokenizeCardByToken } from '../subscriptions/core/subscriptionService';
import { useToast } from '../components/Toats';
import { useThemeMode } from '../../../_metronic/partials';
import { WhiteLabelProvider } from '../subscriptions/components/WhiteLabelProvider';
import { getFileBase64Public } from '../../services/files';
import { toAbsoluteUrl } from '../../../_metronic/helpers';

const STEPS = [
  { num: 1, label: 'Contato', icon: 'bi-person-lines-fill' },
  { num: 2, label: 'Endereço', icon: 'bi-geo-alt-fill' },
  { num: 3, label: 'Pagamento', icon: 'bi-credit-card-2-front' },
];

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const formatCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
};

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export const CardPaymentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toggleToast } = useToast();
  const { mode } = useThemeMode();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [logoSrc, setLogoSrc] = useState<string>('');
  const [logoLoading, setLogoLoading] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [cardError, setCardError] = useState('');

  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [address, setAddress] = useState({ cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' });
  const [card, setCard] = useState({ number: '', expiryMonth: '', expiryYear: '', ccv: '' });

  useEffect(() => {
    if (token) {
      getSubscriptionByToken(token).then((data: any) => {
        setSubscription(data);
        if (data.client) {
          setContact(prev => ({
            name: data.client.nameResponsible || prev.name,
            email: data.client.email || prev.email,
            phone: data.client.cellphone || data.client.telephone || prev.phone,
          }));
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [token]);

  useEffect(() => {
    const company = subscription?.unitBranch?.company;
    if (company) {
      const imgKey = mode === 'dark' && company.image_dark ? company.image_dark : company.image;
      if (imgKey) {
        if (imgKey.includes('/') || imgKey.startsWith('data:')) {
          setLogoSrc(imgKey);
          setLogoLoading(false);
        } else {
          setLogoLoading(true);
          getFileBase64Public(imgKey).then((res: any) => {
            if (res && res.base64) {
              setLogoSrc(`data:image/png;base64,${res.base64}`);
            } else {
              setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
            }
            setLogoLoading(false);
          }).catch(() => {
            setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
            setLogoLoading(false);
          });
        }
      } else {
        setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
        setLogoLoading(false);
      }
    } else {
      if (subscription) {
        setLogoSrc(mode === 'dark' ? toAbsoluteUrl('/media/imgs/novalogo.png') : toAbsoluteUrl('/media/imgs/novaLogoBlack.png'));
        setLogoLoading(false);
      }
    }
  }, [subscription, mode]);

  const validateStep = (step: number): boolean => {
    setCardError('');
    if (step === 1 && (!contact.name || !contact.email || !contact.phone)) {
      setCardError('Preencha todos os campos de contato.');
      return false;
    }
    if (step === 2 && (!address.cep || !address.street || !address.number || !address.neighborhood || !address.city || !address.state)) {
      setCardError('Preencha o endereço completo.');
      return false;
    }
    if (step === 3 && (!card.number || !card.expiryMonth || !card.expiryYear || !card.ccv)) {
      setCardError('Preencha todos os dados do cartão.');
      return false;
    }
    return true;
  };

  const handleNext = () => { if (validateStep(currentStep)) setCurrentStep(s => Math.min(s + 1, 3)); };
  const handlePrev = () => { setCurrentStep(s => Math.max(s - 1, 1)); setCardError(''); };

  const handleConfirmPayment = async () => {
    if (!token || !validateStep(3)) return;
    setSubmitting(true);
    try {
      await tokenizeCardByToken(token, {
        creditCard: {
          holderName: contact.name,
          number: card.number.replace(/\D/g, ''),
          expiryMonth: card.expiryMonth,
          expiryYear: card.expiryYear,
          ccv: card.ccv,
        },
        creditCardHolderInfo: {
          name: contact.name,
          email: contact.email,
          phone: contact.phone.replace(/\D/g, ''),
          postalCode: address.cep.replace(/\D/g, ''),
          addressNumber: address.number,
          addressComplement: address.complement || undefined,
        },
      });
      await checkoutSubscription(token, { coupon_code: null, items: [], payment_method: 'CARTAO' });
      navigate(`/checkout/${token}/success`, { state: { paymentMethod: 'CARTAO' } });
    } catch (err: any) {
      setCardError(err?.response?.data?.message || err?.response?.data?.error || 'Erro ao processar pagamento.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className='d-flex flex-column flex-root bg-body h-100' style={{ minHeight: '100vh' }}>
        <div className='d-flex flex-column flex-center w-100 p-10'>
          <div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Carregando...</span></div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className='d-flex flex-column flex-root bg-body h-100' style={{ minHeight: '100vh' }}>
        <div className='d-flex flex-column flex-center w-100 p-10'>
          <h1 className='text-gray-800 fw-bolder mb-3'>Link Inválido ou Expirado</h1>
          <div className='text-muted fw-bold fs-5'>Verifique o link recebido e tente novamente.</div>
        </div>
      </div>
    );
  }

  const company = subscription.unitBranch?.company;
  const cycleMultipliers: Record<string, number> = { MENSAL: 1, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 };
  const monthly = Number(subscription.monthly_value) || 0;
  const adhesion = Number(subscription.adhesion_fee) || 0;
  const total = (monthly * (cycleMultipliers[subscription.cycle] || 1)) + adhesion;

  const StepIcon = ({ step }: { step: typeof STEPS[0] }) => {
    const isCompleted = currentStep > step.num;
    const isActive = currentStep === step.num;
    return (
      <div className={`d-flex align-items-center justify-content-center rounded-circle ${isCompleted ? 'bg-success' : isActive ? 'bg-primary' : 'bg-light border border-gray-300'}`} style={{ width: 36, height: 36 }}>
        {isCompleted ? (
          <i className='bi bi-check-lg fs-6 text-white'></i>
        ) : (
          <i className={`${step.icon} fs-6 ${isActive ? 'text-white' : 'text-gray-400'}`}></i>
        )}
      </div>
    );
  };

  return (
    <WhiteLabelProvider company={company}>
      <div className='d-flex flex-column flex-root bg-body h-100' style={{ minHeight: '100vh' }}>
        <div className='d-flex flex-column flex-center w-100 p-10'>
          <div className='card card-flush shadow-sm mw-800px w-100'>
            <div className='card-header align-items-center border-0 pt-8 pb-0 px-9'>
              <div>
                <h2 className='text-gray-800 fw-bolder mb-1'>Pagamento com Cartão</h2>
                <span className='text-gray-500 fw-bold fs-7'>Etapa {currentStep} de 3</span>
              </div>
              <div className='card-toolbar'>
                {logoLoading ? <span className='spinner-border spinner-border-sm text-muted'></span> : (logoSrc && <img alt='Logo' src={logoSrc} style={{ height: 28 }} />)}
              </div>
            </div>

            <div className='px-9 pt-6 pb-2'>
              <div className='d-flex align-items-center mx-6 mb-2'>
                {STEPS.map((step, idx) => (
                  <React.Fragment key={step.num}>
                    <div className='d-flex flex-column align-items-center' style={{ flexShrink: 0 }}>
                      <StepIcon step={step} />
                      <span className={`fs-8 fw-bold mt-1 ${currentStep >= step.num ? 'text-gray-800' : 'text-gray-400'}`}>{step.label}</span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className='flex-grow-1 d-flex align-items-center mx-3 mb-4'>
                        <div className={`w-100 rounded`} style={{ height: 2, backgroundColor: currentStep > step.num ? 'var(--bs-success)' : 'var(--bs-gray-300)' }}></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className='mx-9 mb-4'>
              <div className='card card-bordered flex-grow-1'>
                <div className='card-body p-7'>

                  {currentStep === 1 && (
                    <>
                      <div className='mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Nome completo</label>
                        <input type='text' className='form-control form-control-solid' placeholder='Nome do titular do cartão' value={contact.name} onChange={(e) => setContact({...contact, name: e.target.value})} />
                      </div>
                      <div className='mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Email</label>
                        <input type='email' className='form-control form-control-solid' placeholder='seu@email.com' value={contact.email} onChange={(e) => setContact({...contact, email: e.target.value})} />
                      </div>
                      <div className='mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Telefone (com DDD)</label>
                        <input type='tel' className='form-control form-control-solid' placeholder='(11) 90000-0000' value={formatPhone(contact.phone)} onChange={(e) => setContact({...contact, phone: e.target.value})} />
                      </div>
                    </>
                  )}

                  {currentStep === 2 && (
                    <div className='row'>
                      <div className='col-md-4 mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>CEP</label>
                        <input type='text' className='form-control form-control-solid' placeholder='00000-000' value={formatCep(address.cep)} onChange={(e) => setAddress({...address, cep: e.target.value})} maxLength={9} />
                      </div>
                      <div className='col-md-8 mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Logradouro</label>
                        <input type='text' className='form-control form-control-solid' placeholder='Rua, Avenida...' value={address.street} onChange={(e) => setAddress({...address, street: e.target.value})} />
                      </div>
                      <div className='col-4 mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Número</label>
                        <input type='text' className='form-control form-control-solid' placeholder='123' value={address.number} onChange={(e) => setAddress({...address, number: e.target.value})} />
                      </div>
                      <div className='col-8 mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Complemento</label>
                        <input type='text' className='form-control form-control-solid' placeholder='Apto, Bloco (opcional)' value={address.complement} onChange={(e) => setAddress({...address, complement: e.target.value})} />
                      </div>
                      <div className='col-md-5 mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Bairro</label>
                        <input type='text' className='form-control form-control-solid' placeholder='Centro' value={address.neighborhood} onChange={(e) => setAddress({...address, neighborhood: e.target.value})} />
                      </div>
                      <div className='col-md-4 mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Cidade</label>
                        <input type='text' className='form-control form-control-solid' placeholder='São Paulo' value={address.city} onChange={(e) => setAddress({...address, city: e.target.value})} />
                      </div>
                      <div className='col-md-3 mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Estado</label>
                        <select className='form-select form-select-solid' value={address.state} onChange={(e) => setAddress({...address, state: e.target.value})}>
                          <option value=''>UF</option>
                          {UFS.map(uf => (<option key={uf} value={uf}>{uf}</option>))}
                        </select>
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <>
                      <div className='mb-5'>
                        <label className='form-label fw-bolder text-gray-800'>Número do Cartão</label>
                        <input type='text' className='form-control form-control-solid' placeholder='0000 0000 0000 0000' value={formatCardNumber(card.number)} onChange={(e) => setCard({...card, number: e.target.value})} maxLength={19} />
                      </div>
                      <div className='row mb-5'>
                        <div className='col-5'>
                          <label className='form-label fw-bolder text-gray-800'>Mês</label>
                          <input type='text' className='form-control form-control-solid' placeholder='MM' value={card.expiryMonth} onChange={(e) => setCard({...card, expiryMonth: e.target.value})} maxLength={2} />
                        </div>
                        <div className='col-5'>
                          <label className='form-label fw-bolder text-gray-800'>Ano</label>
                          <input type='text' className='form-control form-control-solid' placeholder='AAAA' value={card.expiryYear} onChange={(e) => setCard({...card, expiryYear: e.target.value})} maxLength={4} />
                        </div>
                        <div className='col-2'>
                          <label className='form-label fw-bolder text-gray-800'>CVV</label>
                          <input type='text' className='form-control form-control-solid' placeholder='123' value={card.ccv} onChange={(e) => setCard({...card, ccv: e.target.value})} maxLength={4} />
                        </div>
                      </div>
                    </>
                  )}

                  {cardError && (
                    <div className='d-flex align-items-center bg-light-danger rounded p-3 mt-5'>
                      <i className='bi bi-exclamation-triangle-fill text-danger fs-5 me-3'></i>
                      <span className='text-danger fw-bold fs-7'>{cardError}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className='d-flex gap-3 mt-5'>
                {currentStep > 1 ? (
                  <button className='btn btn-light btn-active-light-primary fw-bold flex-grow-1' onClick={handlePrev} disabled={submitting}>
                    <i className='bi bi-arrow-left me-2'></i>Voltar
                  </button>
                ) : (
                  <button className='btn btn-light btn-active-light-primary fw-bold flex-grow-1' onClick={() => navigate(`/checkout/${token}`)}>
                    <i className='bi bi-arrow-left me-2'></i>Cancelar
                  </button>
                )}
                {currentStep < 3 ? (
                  <button className='btn btn-primary fw-bold flex-grow-1' onClick={handleNext}>
                    Continuar<i className='bi bi-arrow-right ms-2'></i>
                  </button>
                ) : (
                  <button className='btn btn-primary fw-bold flex-grow-1' onClick={handleConfirmPayment} disabled={submitting}>
                    {submitting ? (
                      <><span className='spinner-border spinner-border-sm align-middle me-2'></span>Processando...</>
                    ) : (
                      <><i className='bi bi-lock-fill me-2'></i>Pagar R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </WhiteLabelProvider>
  );
};
